import React from 'react'

// Custom Components
import Sitemap from './partials/sitemap-builder' // Ajuste o caminho conforme necessário

// Ui components
import {Box, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'

// Internationalization
import {useIntl} from 'react-intl'

// OOTB Hooks
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Custom hooks
import useMultiSite from '../../hooks/use-multi-site'

// Utils
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// PWA Kit SDK
import {useAccessToken} from '@salesforce/commerce-sdk-react'

// React query
import {useQuery} from '@tanstack/react-query'

// Custom API
import SitemapMgr from '../../custom-api/site-map-custom-api'

// Constants
// Constants
import {MAX_CACHE_AGE_STATIC_PAGES, STALE_WHILE_REVALIDATE} from '@app/constants'

// Aditional links
const additionalLinks = [
    {id: 'store-locator', name: 'Store Locator', url: '/stores'},
    {id: 'brand-homepage', name: 'Brand Homepage', url: '/'},
    {id: 'magazine-homepage', name: 'Magazine Homepage', url: '/page/blog'},
    {id: 'contact-page', name: 'Contact Page', url: '/page/contactus'},
    {id: 'my-account-page', name: 'My Account Page', url: '/login'}
]

/**
 * SitemapPage Component
 *
 * This functional component renders the sitemap page of the application.
 * It utilizes a Box component to provide padding and layout structure.
 * The page includes a heading and a Sitemap component that displays the
 * sitemap data and additional links.
 *
 * @returns {JSX.Element} A React component that renders the sitemap page.
 *
 * @component
 * @example
 * return (
 *   <SitemapPage />
 * )
 */
const SitemapPage = () => {
    const intl = useIntl()
    const {getTokenWhenReady} = useAccessToken()
    const {site, locale} = useMultiSite()

    // Use useQuery to fetch sitemap data
    const {data} = useQuery({
        queryKey: ['sitemapData'],
        queryFn: async () => {
            const accessToken = await getTokenWhenReady()
            const {app: appConfig} = getConfig()
            const api = new SitemapMgr({
                parameters: {
                    siteId: site.id,
                    organizationId: appConfig.commerceAPI.parameters.organizationId,
                    locale: locale.id
                }
            })
            const result = await api.sitemap(site.id, accessToken)
            return result
        }
    })

    const {res} = useServerContext()

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_STATIC_PAGES}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    return (
        <Box p={4} textAlign="center">
            <Heading as="h1" size="2xl" mb={20} textTransform={'uppercase'}>
                {intl.formatMessage({
                    id: 'sitemap.title',
                    defaultMessage: 'Sitemap'
                })}
            </Heading>
            {data?.success && data?.data && (
                <Sitemap data={data?.data} additionalLinks={additionalLinks} />
            )}
        </Box>
    )
}

export default SitemapPage
